import { FC, useMemo, useState } from 'react';
import { ServiceRequestDto, ServiceRequestPayloadSchema } from '@/services/api/contracts/serviceRequests';
import { X } from 'lucide-react';
import { useParams } from 'react-router';
import { useCompany } from '@/services/api/hooks/companies';
import { formatCurrency as globalFormatCurrency } from '@/utils/currency';

export const ServiceRequestPayloadViewer: FC<{ request: ServiceRequestDto; onClose: () => void }> = ({ request, onClose }) => {
  const { partnerId, companyId } = useParams();
  const [company] = useCompany(partnerId || '', companyId || '');
  const [showRawJson, setShowRawJson] = useState(false);

  const { parsedPayload, rawPayload } = useMemo(() => {
    let raw: any = request.payload;
    try {
      raw = typeof request.payload === 'string' ? JSON.parse(request.payload) : request.payload;
    } catch {
      // ignore
    }

    const parseResult = ServiceRequestPayloadSchema.safeParse(raw);
    return {
      rawPayload: raw,
      parsedPayload: parseResult.success ? parseResult.data : null
    };
  }, [request.payload]);

  const formatCurrency = (cents?: number | null) => {
    if (cents == null) return '-';
    return globalFormatCurrency(cents / 100, company?.region?.code);
  };

  const formatDate = (isoString?: string | null) => {
    if (!isoString) return '-';
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return isoString;
      return new Intl.DateTimeFormat('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }).format(d);
    } catch {
      return isoString;
    }
  };

  if (showRawJson || !parsedPayload) {
    return (
      <div className="w-full h-full p-6 bg-white overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4 shrink-0">
          <h2 className="text-2xl font-bold">Service Request Payload: {request.requestType}</h2>
          <div className="flex gap-2">
            {parsedPayload && (
              <button onClick={() => setShowRawJson(false)} className="px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 rounded-md transition-colors">
                View Parsed UI
              </button>
            )}
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 rounded-md transition-colors">
              Close
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <pre className="bg-gray-900 text-green-400 p-4 rounded-md overflow-x-auto text-sm font-mono min-h-full">
            <code>{JSON.stringify(rawPayload, null, 2)}</code>
          </pre>
        </div>
      </div>
    );
  }

  const { requestContext, userContext, financialContext, deployment, collection } = parsedPayload;

  const deployActive = deployment?.isRequired;
  const collectActive = collection?.isRequired;

  const renderLogisticsMetadata = (logistics: any) => {
    if (!logistics) return null;

    const addressParts = [];
    if (logistics.address) {
      if (logistics.address.name) addressParts.push(logistics.address.name);
      if (logistics.address.line1) addressParts.push(logistics.address.line1);
      if (logistics.address.line2) addressParts.push(logistics.address.line2);
      if (logistics.address.city) addressParts.push(logistics.address.city);
      if (logistics.address.state) addressParts.push(logistics.address.state);
      if (logistics.address.postcode) addressParts.push(logistics.address.postcode);
      if (logistics.address.country) addressParts.push(logistics.address.country);
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div>
          <span className="block text-gray-500 font-medium mb-1">Address</span>
          {addressParts.length > 0 ? (
            addressParts.map((part, idx) => <span key={idx} className="block">{part}</span>)
          ) : (
            <span>-</span>
          )}
        </div>
        <div>
          <span className="block text-gray-500 font-medium mb-1">Site Contact</span>
          <span>{logistics.siteContact?.contactName || '-'}</span>
          {logistics.siteContact?.contactEmail && <span className="block text-gray-400">{logistics.siteContact.contactEmail}</span>}
          {logistics.siteContact?.contactMobile && <span className="block text-gray-400">{logistics.siteContact.contactMobile}</span>}
        </div>
        <div>
          <div className="mb-4">
            <span className="block text-gray-500 font-medium mb-1">Selected Date</span>
            <span>{formatDate(logistics.schedule?.requestedDate)}</span>
          </div>
          {logistics.notes && (
            <div>
              <span className="block text-gray-500 font-medium mb-1">Notes</span>
              <p className="whitespace-pre-wrap">{logistics.notes}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCard = (title: string, logistics: any, data: typeof deployment, showSerial: boolean) => {
    if (!data) return null;
    return (
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 flex flex-col h-full">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          {data.logistics?.schedule?.isAsapExpedite && (
            <span className="px-2 py-1 text-xs font-bold bg-red-100 text-red-700 rounded-full">ASAP</span>
          )}
        </div>
        
        {logistics && (
          <div className="mb-4">
            {renderLogisticsMetadata(logistics)}
          </div>
        )}

        {data.items && data.items.length > 0 && (
          <div className="overflow-x-auto shadow-sm ring-1 ring-black ring-opacity-5 sm:rounded-lg mt-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-2 pl-4 pr-3 text-left text-xs font-semibold text-gray-900">Qty</th>
                  <th className="py-2 px-3 text-left text-xs font-semibold text-gray-900">Category</th>
                  <th className="py-2 px-3 text-left text-xs font-semibold text-gray-900">SKU</th>
                  {showSerial && <th className="py-2 px-3 text-left text-xs font-semibold text-gray-900">Serial No.</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {data.items.map((item, idx) => (
                  <tr key={idx}>
                    <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-900">{item.quantity}</td>
                    <td className="whitespace-nowrap py-2 px-3 text-sm text-gray-500">{item.category}</td>
                    <td className="whitespace-nowrap py-2 px-3 text-sm text-gray-500">{item.sku || '-'}</td>
                    {showSerial && <td className="whitespace-nowrap py-2 px-3 text-sm text-gray-500">{item.serialNumber || '-'}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full h-full bg-white flex flex-col p-6 overflow-hidden">
      <div className="shrink-0 mb-6 flex justify-between items-start border-b pb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">{requestContext.customerReference || 'N/A'}</h2>
          <p className="text-sm text-gray-500">
            Requested by <span className="font-semibold text-gray-700">{requestContext.requestedBy}</span> on {formatDate(requestContext.raisedDateTime)}
          </p>
          {request.haloTicketId && (
            <p className="text-sm font-medium text-gray-500 mt-1">
              Halo Ticket: #{request.haloTicketId}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pr-2 space-y-6">
        <section>
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 flex flex-col">
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">User Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <span className="block text-xs text-gray-500 mb-1">Name</span>
                <span className="text-sm font-medium text-gray-900">{userContext.userName}</span>
              </div>
              <div>
                <span className="block text-xs text-gray-500 mb-1">Corporate Email</span>
                <span className="text-sm font-medium text-gray-900">{userContext.userEmail || (userContext.noCorporateEmail ? 'None' : '-')}</span>
              </div>
              <div>
                <span className="block text-xs text-gray-500 mb-1">Personal Email</span>
                <span className="text-sm font-medium text-gray-900">{userContext.personalEmail || '-'}</span>
              </div>
              <div>
                <span className="block text-xs text-gray-500 mb-1">Mobile</span>
                <span className="text-sm font-medium text-gray-900">{userContext.userMobile || '-'}</span>
              </div>
            </div>
          </div>
        </section>

        {(deployActive || collectActive) && (
          <section>
            {deployActive && collectActive ? (
              <div className="space-y-6">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold">Logistics Details</h3>
                    {(deployment?.logistics?.schedule?.isAsapExpedite || collection?.logistics?.schedule?.isAsapExpedite) && (
                      <span className="px-2 py-1 text-xs font-bold bg-red-100 text-red-700 rounded-full">ASAP</span>
                    )}
                  </div>
                  {renderLogisticsMetadata(deployment?.logistics || collection?.logistics)}
                </div>
                <div className="grid grid-cols-1 gap-6">
                  {renderCard('Collection Items', null, collection, true)}
                  {renderCard('Deployment Items', null, deployment, false)}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {collectActive && renderCard('Collection', collection.logistics, collection, true)}
                {deployActive && renderCard('Deployment', deployment.logistics, deployment, false)}
              </div>
            )}
          </section>
        )}

        <section>
          <details className="group border border-gray-200 rounded-lg bg-white overflow-hidden">
            <summary className="flex justify-between items-center font-medium cursor-pointer list-none p-4 bg-gray-50 hover:bg-gray-100 transition-colors focus:outline-none">
              <span className="text-sm font-bold text-gray-600 uppercase tracking-wider">Financials</span>
              <span className="font-bold text-gray-900">Est. Total: {formatCurrency(financialContext.totalEstimatedCost)}</span>
            </summary>
            <div className="p-4 bg-white border-t border-gray-200">
              {financialContext.inferredServices && financialContext.inferredServices.length > 0 ? (
                <div className="overflow-x-auto shadow-sm ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-2 pl-4 pr-3 text-left text-xs font-semibold text-gray-900">Service Name</th>
                        <th className="py-2 px-3 text-left text-xs font-semibold text-gray-900">Rate Card ID</th>
                        <th className="py-2 px-3 text-right text-xs font-semibold text-gray-900">Qty</th>
                        <th className="py-2 px-4 text-right text-xs font-semibold text-gray-900">Price</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {financialContext.inferredServices.map((svc, idx) => (
                        <tr key={idx}>
                          <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-900">{svc.name}</td>
                          <td className="whitespace-nowrap py-2 px-3 text-sm text-gray-500">{svc.rateCardId || '-'}</td>
                          <td className="whitespace-nowrap py-2 px-3 text-sm text-gray-900 text-right">{svc.quantity}</td>
                          <td className="whitespace-nowrap py-2 px-4 text-sm text-gray-900 text-right">{formatCurrency(svc.price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No inferred services found.</p>
              )}
            </div>
          </details>
        </section>
      </div>


    </div>
  );
};
